/*
 *  This document is the CONFIDENTIAL PROPERTY of Corero Network Security, Inc.
 *  Copyright (C) 2019-2024 by Corero Network Security, Inc. All rights reserved.
 *  Notice of copyright on this document does not constitute publication.
 */

import moment from 'moment-timezone';

import { store as auth } from '../stores/AuthStore.js';

const isoBackendFormat = 'YYYY-MM-DD HH:mm:ss'; //Backend expects this format
const isoBackendFormatWithoutSeconds = 'YYYY-MM-DDTHH:mm';
const isoFormatShort = 'YYYY-MM-DD HH:mm';
const isoFormatDate = 'YYYY-MM-DD';
const isoFormatTime = 'HH:mm:ss';
const isoFormatTimeShort = 'HH:mm';
const displayFormat = 'D MMMM, YYYY, HH:mm';

/**
 * @returns the current offset of the given IANA timezone name using the moment timezone library.
 */
const timezoneToOffset = (timezone) => moment.tz(timezone).format('Z');

const getTimezoneNames = () => {
    return new Set(moment.tz.names());
}

const isSupported = (tz) => {
    return getTimezoneNames().has(tz);
}

/**
 * Returns the formatted time with the timezone offset
 * @param time     the time
 * @param timezone the timezone
 */
const formatTimeWithTimezone = (time, timezone) => timezone === 'UTC' ?
    `${time} (UTC)` :
    `${time} (UTC${timezoneToOffset(timezone)})`;

/**
 * Formats the timezone label
 *
 * @param timezone the timezone
 * @param location the location
 * @returns {string|string} the formatted timezone label
 */
const formatTimezoneLabel = ({ timezone, location }) => location === 'UTC'
    ? 'UTC'
    : `(UTC${timezoneToOffset(timezone)})${location ? ` ${location}`: ''}`;

/**
 * @returns the current user's set timezone from the authenticated user.
 * @returns {'UTC'} If the current user's set timezone does not exist or is not supported by moment.js
 */
const userTimezone = () => {
    if (auth.user() && auth.user().timeZone && isSupported(auth.user().timeZone)) {
        return auth.user().timeZone
    }
    return 'UTC';
};

/**
 * @return the current utc moment truncated to hours
 */
const currentUtcMomentHour = () => moment.utc().startOf('hour');

/**
 * @returns {object} the current UTC moment (using the moment library's moment.utc() function)
 */
const currentUtcMoment = () => moment.utc();

/**
 * @returns {object} the current moment in the user's set timezone (using {@link userTimezone}.
 */
const currentUserMoment = () => moment.tz(userTimezone());

/**
 * @return {moment.Moment} today's midnight time in the user's timezone
 */
const todayAtMidnight = () => moment.tz(userTimezone()).set("hour", 24).set("minute", 0);

/**
 * @return {moment.Moment} midnight thirty days ago in the user's timezone
 */
const thirtyDaysAgoAtMidnight = () =>  moment.tz(userTimezone()).set("hour", 24).set("minute", 0).subtract(30, "days");


/**
 * CONVERTS a given date to a moment in UTC (using the moment library's moment.utc() function)
 * Optional format parameter defaults to moment.ISO_8601 which is used by the moment library to PARSE the given date
 *  in the case that the date parameter is a string.
 * If the given date does not indicate any timezone then it will be assumed as UTC.
 * @param {number | string | Moment} date the date to convert.
 * @param {string | MomentBuiltinFormat} format the format of the date if the date is a string.
 * @returns {object} the converted date as a moment in UTC.
 */
const utcMoment = (date, format = moment.ISO_8601) => typeof date === 'string' ? moment.utc(date, format) : moment.utc(date);


/**
 * Returns how long ago given timestamp was. If timestamp is after now (due to rounding errors), will return 'just now'.
 *
 * @param time given timestamp in ISO_8601
 * @returns {string} message describing how long ago given timestamp is
 */
const relativeTimeAgo = (time) => {
    const past = utcMoment(time);
    const now = currentUtcMoment();
    return past.diff(now) > 0 ? "just now" : past.from(now);
}


/**
 * CONVERTS a given date to a moment in the current user's set timezone (using the moment timezone library's tz() function)
 * Optional format parameter defaults to moment.ISO_8601 which is used by the {@link utcMoment} function
 * If the given date does not indicate any timezone then it will be assumed as UTC (by the {@link utcMoment} function.
 * @param {number | string | Moment} date the date to convert.
 * @param format the format of the date if the date is a string (defaults to ISO format if no format is given).
 * @param timezone the timezone to convert the given date to (defaults to the user's set timezone if no timezone is given)
 * @returns {object} the converted date as a moment in the current user's set timezone.
 */
const userMoment = (date, format = moment.ISO_8601, timezone = userTimezone()) => utcMoment(date, format).tz(timezone);

/**
 * @param val value to be converted into HH:mm:ss format
 * @param unit unit of measurement that {@param val} is in
 * @returns {string} val parameter in "xd xh xm xs" format
 * where {x} is a numeric value and {d, h, m, s} represents
 * time units.
 */
const formatDuration = (val, unit = 'seconds') => {
    if (val < 0) {
        return '0d 0h 0m 0s';
    }

    // moment.js considers average durations to account for leap years, and performs round-ups and round-downs when
    // calculating years / months / days from seconds, which results to some inconsistencies, e.g:
    // 31622400 seconds = 366 days = 1 year
    // 63244800 seconds = 732 days = 2 years and 1 day
    const duration = moment.duration(val, unit);

    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    if (duration.asYears() < 1) {
        const days = Math.floor(duration.asDays());
        return `${days}d ${hours}h ${minutes}m ${seconds}s`
    }

    const years = Math.floor(duration.asYears());
    const days = duration.days();
    return `${years}y ${days}d ${hours}h ${minutes}m ${seconds}s`;
};

/**
 * Formats the given eventTime value to be used in charts
 *
 * @param val the eventTime value
 * @returns the formatted eventTime
 */
const formatChartEventTime = (val) => {
    const dateAndTime = val.split(" ");
    return `${dateAndTime[0]}\n${dateAndTime[1]}`
}

/**
 * @param {number | string | Moment} date the date to calculate the days until from the current date.
 * @param format the format of the date if the date is a string
 * @returns {number} the number of days from the current date until the date given.
 */
const daysUntil = (date, format = moment.ISO_8601) => {
    const now = currentUtcMoment().startOf('day');
    const then = typeof date === 'string' ? utcMoment(date, format).startOf('day') : utcMoment(date).startOf('day');
    return then.diff(now, 'days');
};


/**
 * Filters a list of IANA timezones to return only timezones supported by moment.js
 * @param timezones list of timezones
 * @return filtered list of timezones
 */
const filterSupported = (timezones) => {
    const supported = getTimezoneNames();
    return timezones.filter(tz => supported.has(tz.timezone));
}



export {
    isoBackendFormat,
    isoBackendFormatWithoutSeconds,
    isoFormatShort,
    isoFormatDate,
    isoFormatTime,
    isoFormatTimeShort,
    displayFormat,
    formatTimeWithTimezone,
    userTimezone,
    formatTimezoneLabel,
    timezoneToOffset,
    userMoment,
    currentUserMoment,
    utcMoment,
    currentUtcMoment,
    currentUtcMomentHour,
    formatDuration,
    formatChartEventTime,
    daysUntil,
    relativeTimeAgo,
    todayAtMidnight,
    thirtyDaysAgoAtMidnight,
    filterSupported
};
